import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface Material {
  id: string;
  name: string;
}

export function MaterialConfig() {
  const [materials, setMaterials] = useState<Material[]>([
    { id: "1", name: "Aço Carbono" },
    { id: "2", name: "Alumínio" },
    { id: "3", name: "Aço Inox" },
  ]);
  const [newMaterial, setNewMaterial] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleAdd = () => {
    if (!newMaterial.trim()) {
      toast({
        title: "Erro",
        description: "O nome do material não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (materials.some(material => material.name.toLowerCase() === newMaterial.toLowerCase())) {
      toast({
        title: "Erro",
        description: "Já existe um material com este nome.",
        variant: "destructive",
      });
      return;
    }

    setMaterials([...materials, { id: Date.now().toString(), name: newMaterial }]);
    setNewMaterial("");
    toast({
      title: "Sucesso",
      description: "Material adicionado com sucesso.",
    });
  };

  const handleEdit = (id: string, newName: string) => {
    if (!newName.trim()) {
      toast({
        title: "Erro",
        description: "O nome do material não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    if (materials.some(material => material.name.toLowerCase() === newName.toLowerCase() && material.id !== id)) {
      toast({
        title: "Erro",
        description: "Já existe um material com este nome.",
        variant: "destructive",
      });
      return;
    }

    setMaterials(materials.map(material => 
      material.id === id ? { ...material, name: newName } : material
    ));
    setEditingId(null);
    toast({
      title: "Sucesso",
      description: "Material atualizado com sucesso.",
    });
  };

  const handleDelete = (id: string) => {
    setMaterials(materials.filter(material => material.id !== id));
    toast({
      title: "Sucesso",
      description: "Material removido com sucesso.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Input
          placeholder="Novo material"
          value={newMaterial}
          onChange={(e) => setNewMaterial(e.target.value)}
          className="max-w-sm bg-[#222831] text-white border-[#404040]"
        />
        <Button onClick={handleAdd} className="bg-[#0073B7] hover:bg-[#004080] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Adicionar
        </Button>
      </div>

      <div className="bg-[#1A1F2C] rounded-md border border-[#404040]">
        <div className="divide-y divide-[#404040]">
          {materials.map((material) => (
            <div key={material.id} className="p-4 flex items-center justify-between text-white hover:bg-[#222831]">
              {editingId === material.id ? (
                <Input
                  defaultValue={material.name}
                  onBlur={(e) => handleEdit(material.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleEdit(material.id, e.currentTarget.value);
                    }
                  }}
                  className="max-w-sm bg-[#222831] text-white border-[#404040]"
                  autoFocus
                />
              ) : (
                <span>{material.name}</span>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingId(material.id)}
                  className="hover:bg-[#222831]"
                >
                  <Pencil className="h-4 w-4 text-[#0073B7]" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(material.id)}
                  className="hover:bg-[#222831]"
                >
                  <Trash2 className="h-4 w-4 text-[#E60026]" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}